import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import Container from '../../components/Container';
import Main from '../../layouts/Main';
import { useAppSelector } from '../../hooks/hooks';
import { Compound } from '../../services/restserver-openapi';
import { getCompounds } from '../../utils/Host/offer';
import {
    addCompoundToLabel,
    AgentLabel,
    createLabel,
    deleteLabel,
    getCompoundLabel,
    getLabels,
    removeCompoundFromLabel,
    updateLabel,
} from '../../utils/Agent/labels';
import { compressImageFileToBase64 } from '../../utils/imageCompression';

const emptyLabel = (): AgentLabel => ({
    labelId: 0,
    labelName: '',
    logo: null,
    compoundIds: [],
});

const getSafeCompoundId = (compound: Compound): number => compound.compoundId ?? 0;
const getSafeCompoundName = (compound: Compound): string => compound.name ?? '';
const getSafeHostId = (compound: Compound): number => compound.hostId ?? 0;

const AgentLabelOverviewPage = (): JSX.Element => {
    const theme = useTheme();
    const mode = theme.palette.mode;
    const isVerifiedAgent = useAppSelector((state) => state.user.isVerifiedAgent);

    const [labels, setLabels] = useState<AgentLabel[]>([]);
    const [selectedLabel, setSelectedLabel] = useState<AgentLabel>(emptyLabel());
    const [allCompounds, setAllCompounds] = useState<Compound[]>([]);
    const [compoundSearch, setCompoundSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [logoPreview, setLogoPreview] = useState('');

    const compoundMap = useMemo(() => new Map(allCompounds.map((compound) => [compound.compoundId, compound])), [allCompounds]);

    const selectedCompounds = useMemo(
        () => selectedLabel.compoundIds
            .map((compoundId) => compoundMap.get(compoundId))
            .filter((compound): compound is Compound => typeof compound !== 'undefined'),
        [compoundMap, selectedLabel.compoundIds]
    );

    const availableCompounds = useMemo(() => {
        const normalizedSearch = compoundSearch.trim().toLowerCase();

        return allCompounds.filter((compound) => {
            const matchesSearch = !normalizedSearch
                || getSafeCompoundName(compound).toLowerCase().includes(normalizedSearch)
                || `${getSafeCompoundId(compound)}`.includes(normalizedSearch)
                || `${getSafeHostId(compound)}`.includes(normalizedSearch);

            return matchesSearch;
        });
    }, [allCompounds, compoundSearch]);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setError('');
                const [loadedLabels, loadedCompounds] = await Promise.all([
                    getLabels(),
                    getCompounds(0, 0, undefined, undefined, []),
                ]);

                setLabels(loadedLabels);
                setAllCompounds(loadedCompounds);
                const firstLabel = loadedLabels[0] ?? emptyLabel();
                setSelectedLabel(firstLabel);
                setLogoPreview(firstLabel.logo ? `data:image/*;base64,${firstLabel.logo}` : '');
            } catch (loadError) {
                setError('Die Labels konnten nicht geladen werden.');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    const upsertLabel = (label: AgentLabel) => {
        setLabels((prevState) => {
            const existingIndex = prevState.findIndex((entry) => entry.labelId === label.labelId);

            if (existingIndex === -1) {
                return [label, ...prevState];
            }

            const nextState = [...prevState];
            nextState[existingIndex] = label;
            return nextState;
        });

        setSelectedLabel(label);
        setLogoPreview(label.logo ? `data:image/*;base64,${label.logo}` : '');
    };

    const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        try {
            const base64Logo = await compressImageFileToBase64(file, {
                maxWidth: 512,
                maxHeight: 512,
                maxBytes: 250_000,
            });
            setSelectedLabel((prevState) => ({
                ...prevState,
                logo: base64Logo,
            }));
            setLogoPreview(URL.createObjectURL(file));
        } catch (imageError) {
            setError('Das Logo konnte nicht geladen werden.');
        }
    };

    const handleSave = async () => {
        if (!selectedLabel.labelName.trim()) {
            setError('Bitte gib einen Namen fuer das Label ein.');
            return;
        }

        try {
            setSaving(true);
            setError('');
            const savedLabel = selectedLabel.labelId === 0
                ? await createLabel(selectedLabel)
                : await updateLabel(selectedLabel);

            upsertLabel(savedLabel);
            setSuccessMessage('Label gespeichert.');
        } catch (saveError) {
            setError('Das Label konnte nicht gespeichert werden.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (selectedLabel.labelId === 0) {
            setSelectedLabel(emptyLabel());
            return;
        }

        try {
            setSaving(true);
            setError('');
            await deleteLabel(selectedLabel.labelId);
            const nextLabels = labels.filter((label) => label.labelId !== selectedLabel.labelId);
            setLabels(nextLabels);
            const nextLabel = nextLabels[0] ?? emptyLabel();
            setSelectedLabel(nextLabel);
            setLogoPreview(nextLabel.logo ? `data:image/*;base64,${nextLabel.logo}` : '');
            setSuccessMessage('Label geloescht.');
        } catch (deleteError) {
            setError('Das Label konnte nicht geloescht werden.');
        } finally {
            setSaving(false);
        }
    };

    const toggleCompound = async (compoundId: number) => {
        const alreadySelected = selectedLabel.compoundIds.includes(compoundId);

        if (selectedLabel.labelId === 0) {
            setSelectedLabel((prevState) => ({
                ...prevState,
                compoundIds: alreadySelected
                    ? prevState.compoundIds.filter((id) => id !== compoundId)
                    : [...prevState.compoundIds, compoundId],
            }));
            return;
        }

        try {
            setSaving(true);
            setError('');
            const updatedLabel = alreadySelected
                ? await removeCompoundFromLabel(selectedLabel.labelId, compoundId)
                : await addCompoundToLabel(selectedLabel.labelId, compoundId);
            upsertLabel(updatedLabel);
        } catch (compoundError) {
            setError('Die Compound-Zuordnung konnte nicht aktualisiert werden.');
        } finally {
            setSaving(false);
        }
    };

    const ActionRow = (props: {
        icon: React.ReactNode;
        title: string;
        subtitle?: string;
        active?: boolean;
        onClick?: () => void;
        color?: 'inherit' | 'error';
    }) => {
        const { icon, title, subtitle, active, onClick, color = 'inherit' } = props;

        return (
            <ListItem disablePadding>
                <ListItemButton
                    onClick={onClick}
                    selected={active}
                    sx={{
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                        gap: 0.5,
                        '&.Mui-selected': {
                            bgcolor: 'action.selected',
                            '&:hover': { bgcolor: 'action.selected' },
                        },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: color === 'error' ? 'error.main' : active ? 'primary.main' : 'text.secondary' }}>
                        {icon}
                    </ListItemIcon>

                    <ListItemText
                        primary={
                            <Typography sx={{ fontWeight: 800 }} noWrap>
                                {title}
                            </Typography>
                        }
                        secondary={subtitle}
                        secondaryTypographyProps={{ color: 'text.secondary' }}
                    />
                </ListItemButton>
            </ListItem>
        );
    };

    if (!isVerifiedAgent) {
        return (
            <Main>
                <Container>
                    <Paper elevation={0} sx={{ borderRadius: 4, p: 2, border: 0, boxShadow: 'none', bgcolor: 'background.paper' }}>
                        <Alert severity="error">
                            Sie haben keine Agent-Berechtigung. Womöglich ist Ihr Account noch nicht freigeschaltet.
                        </Alert>
                    </Paper>
                </Container>
            </Main>
        );
    }

    return (
        <Main>
            <Container>
                <Typography sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: 1 }} gutterBottom color="text.secondary">
                    Administration
                </Typography>

                <Box mb={3}>
                    <Typography variant="h3" component="h2" color="text.primary" fontWeight={700} lineHeight={1.2}>
                        Labels{' '}
                        <Typography
                            component="span"
                            variant="inherit"
                            color="text.primary"
                            sx={{
                                background: `linear-gradient(180deg, transparent 82%, ${alpha(
                                    mode === 'light' ? theme.palette.secondary.main : theme.palette.primary.main,
                                    0.3
                                )} 0%)`,
                            }}
                        >
                            & Awards
                        </Typography>
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Lege Auszeichnungen mit Logo an und ordne Compounds analog zu Curations zu.
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2 }}>
                    <Paper elevation={0} sx={{ borderRadius: 4, p: 2, boxShadow: 'none', bgcolor: 'background.paper' }}>
                        <Typography variant="caption" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 1 }} color="text.secondary">
                            Actions
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />

                        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                            <ActionRow
                                icon={<AddCircleOutlineRoundedIcon />}
                                title="Neues Label"
                                subtitle="Leeres Label anlegen"
                                active={selectedLabel.labelId === 0}
                                onClick={() => {
                                    setSuccessMessage('');
                                    setSelectedLabel(emptyLabel());
                                    setLogoPreview('');
                                }}
                            />
                            <ActionRow
                                icon={<DeleteOutlineRoundedIcon />}
                                title={selectedLabel.labelId === 0 ? 'Verwerfen' : 'Label löschen'}
                                subtitle={selectedLabel.labelId === 0 ? 'Neue Eingabe zurücksetzen' : 'Aktives Label entfernen'}
                                onClick={handleDelete}
                                color="error"
                            />
                        </List>

                        <Typography variant="caption" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 1 }} color="text.secondary">
                            Labels
                        </Typography>
                        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
                            {labels.map((label) => {
                                const active = selectedLabel.labelId === label.labelId;

                                return (
                                    <ActionRow
                                        key={label.labelId}
                                        icon={<LabelRoundedIcon />}
                                        title={label.labelName}
                                        subtitle={`${label.compoundIds.length} Compounds`}
                                        active={active}
                                        onClick={() => {
                                            setSuccessMessage('');
                                            upsertLabel(label);
                                        }}
                                    />
                                );
                            })}
                            {!loading && labels.length === 0 && (
                                <Typography color="text.secondary">Noch keine Labels vorhanden.</Typography>
                            )}
                        </List>
                    </Paper>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(360px, 1fr) minmax(520px, 1.25fr)' }, gap: 2 }}>
                        <Paper elevation={0} sx={{ borderRadius: 4, p: 2, boxShadow: 'none', bgcolor: 'background.paper' }}>
                            <Stack spacing={2}>
                                <Typography variant="h6" fontWeight={700}>Editor</Typography>
                                <TextField
                                    label="Name des Labels"
                                    value={selectedLabel.labelName}
                                    onChange={(event) => setSelectedLabel({ ...selectedLabel, labelName: event.target.value })}
                                    fullWidth
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                    <Avatar
                                        src={logoPreview || undefined}
                                        variant="rounded"
                                        sx={{ width: 96, height: 96 }}
                                    />
                                    <Stack spacing={1}>
                                        <Button variant="outlined" component="label">
                                            Logo hochladen
                                            <input hidden type="file" accept="image/*" onChange={handleLogoChange} />
                                        </Button>
                                        <Button
                                            variant="text"
                                            color="inherit"
                                            onClick={() => {
                                                setSelectedLabel((prevState) => ({ ...prevState, logo: null }));
                                                setLogoPreview('');
                                            }}
                                        >
                                            Logo entfernen
                                        </Button>
                                    </Stack>
                                </Stack>
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Zugeordnete Compounds</Typography>
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {selectedCompounds.map((compound) => (
                                            <Chip
                                                key={getSafeCompoundId(compound)}
                                                label={getCompoundLabel(compound)}
                                                onDelete={() => toggleCompound(getSafeCompoundId(compound))}
                                            />
                                        ))}
                                        {selectedCompounds.length === 0 && (
                                            <Typography color="text.secondary">Noch keine Compounds zugeordnet.</Typography>
                                        )}
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                                        {saving ? 'Speichern...' : 'Speichern'}
                                    </Button>
                                    <Button variant="outlined" color="error" onClick={handleDelete} disabled={saving}>
                                        {selectedLabel.labelId === 0 ? 'Verwerfen' : 'Loeschen'}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>

                        <Paper elevation={0} sx={{ borderRadius: 4, p: 2, boxShadow: 'none', bgcolor: 'background.paper' }}>
                            <Stack spacing={2}>
                                <Typography variant="h6" fontWeight={700}>Compounds</Typography>
                                <TextField
                                    label="Nach Name, Compound-ID oder Host-ID suchen"
                                    value={compoundSearch}
                                    onChange={(event) => setCompoundSearch(event.target.value)}
                                    fullWidth
                                />
                                <Box sx={{ maxHeight: 640, overflowY: 'auto' }}>
                                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: 'none', boxShadow: 'none', overflow: 'hidden', bgcolor: 'background.paper' }}>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow sx={{ '& th': { bgcolor: 'background.paper', borderBottomColor: 'divider' } }}>
                                                    {['Id', 'Name', 'HostId', 'Typ', 'Revised', 'Visible', 'Page', 'Label'].map((header) => (
                                                        <TableCell key={header}>
                                                            <Typography color="text.secondary" variant="caption" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                                                {header}
                                                            </Typography>
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {availableCompounds.map((compound) => {
                                                    const compoundId = getSafeCompoundId(compound);
                                                    const selected = selectedLabel.compoundIds.includes(compoundId);

                                                    return (
                                                        <TableRow key={compoundId} hover>
                                                            <TableCell>{compoundId}</TableCell>
                                                            <TableCell sx={{ fontWeight: 800 }}>{getSafeCompoundName(compound)}</TableCell>
                                                            <TableCell>{getSafeHostId(compound)}</TableCell>
                                                            <TableCell>{compound.compoundType ?? ''}</TableCell>
                                                            <TableCell align="center">
                                                                {compound.revised ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {compound.visibleCompound ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    startIcon={<InfoIcon />}
                                                                    sx={{ borderRadius: 2, fontWeight: 800 }}
                                                                    onClick={() => window.open(`/vanlifer/Campsite/${compoundId}`, '_blank', 'noopener,noreferrer')}
                                                                >
                                                                    Details
                                                                </Button>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Tooltip title={selected ? 'Vom Label entfernen' : 'Dem Label hinzufügen'}>
                                                                    <Button
                                                                        variant={selected ? 'outlined' : 'contained'}
                                                                        size="small"
                                                                        onClick={() => toggleCompound(compoundId)}
                                                                    >
                                                                        {selected ? 'Entfernen' : 'Hinzufuegen'}
                                                                    </Button>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                                {availableCompounds.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={8}>
                                                            <Typography color="text.secondary">Keine Compounds gefunden.</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Stack>
                        </Paper>
                    </Box>
                </Box>
            </Container>
        </Main>
    );
};

export default AgentLabelOverviewPage;
